/**
 * Gateway Packages management API service
 *
 * Provides methods for managing Cloud Gateway agent packages.
 * Includes upload, versioning, activation, and download functionality.
 */

import { apiClient } from './api'

// ===== Types =====

export type GatewayPackage = {
  id: string // UUID
  version: string
  filename: string
  storage_type: string
  size_bytes: number
  size_human: string
  sha256_hash: string
  is_active: boolean
  is_latest: boolean
  release_notes: string | null
  download_url: string
  created_at: string
  uploaded_by_id: string // UUID
  uploaded_by_name: string
}

export type GatewayPackageListResponse = {
  packages: GatewayPackage[]
  total: number
  active_version: string | null
}

export type GatewayPackageUpload = {
  version: string
  release_notes?: string
  set_as_active?: boolean
}

export type GatewayPackageActivate = {
  set_as_latest?: boolean
}

// ===== API Functions =====

const gatewayPackagesApi = {
  /**
   * Upload a new gateway package
   */
  uploadPackage: async (file: File, data: GatewayPackageUpload): Promise<GatewayPackage> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('version', data.version)
    if (data.release_notes) {
      formData.append('release_notes', data.release_notes)
    }
    formData.append('set_as_active', String(data.set_as_active ?? true))

    return apiClient.post('/api/v1/gateway-packages/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
  },

  /**
   * List all gateway packages
   */
  listPackages: (): Promise<GatewayPackageListResponse> => {
    return apiClient.get('/api/v1/gateway-packages')
  },

  /**
   * Get the currently active package
   */
  getActivePackage: (): Promise<GatewayPackage> => {
    return apiClient.get('/api/v1/gateway-packages/active')
  },

  /**
   * Get a specific package by ID
   */
  getPackage: (id: string): Promise<GatewayPackage> => {
    return apiClient.get(`/api/v1/gateway-packages/${id}`)
  },

  /**
   * Activate a package for downloads
   */
  activatePackage: (id: string, data?: GatewayPackageActivate): Promise<GatewayPackage> => {
    return apiClient.post(`/api/v1/gateway-packages/${id}/activate`, data || {})
  },

  /**
   * Delete a package
   */
  deletePackage: (id: string): Promise<void> => {
    return apiClient.delete(`/api/v1/gateway-packages/${id}`)
  },

  /**
   * Get download URL for a package
   */
  getDownloadUrl: (id: string): string => {
    return `/api/v1/gateway-packages/${id}/download`
  },
}

// ===== Helper Functions =====

export const getGatewayPackageStatusColor = (pkg: GatewayPackage): { bg: string; text: string } => {
  if (pkg.is_active) {
    return { bg: 'bg-green-100', text: 'text-green-700' }
  }
  if (pkg.is_latest) {
    return { bg: 'bg-blue-100', text: 'text-blue-700' }
  }
  return { bg: 'bg-gray-100', text: 'text-gray-700' }
}

export const getGatewayPackageStatusLabel = (pkg: GatewayPackage): string => {
  if (pkg.is_active) return 'Ativo'
  if (pkg.is_latest) return 'Mais recente'
  return 'Disponível'
}

export default gatewayPackagesApi
